// src/components/lp/Summary.tsx
import React from 'react';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  useMediaQuery, // useMediaQueryを追加
} from '@mui/material';
import DraftsIcon from '@mui/icons-material/Drafts';
import { styled } from '@mui/system';
import { Swiper, SwiperSlide } from 'swiper/react'; // Swiperをインポート
import { Pagination } from 'swiper/modules'; // Paginationモジュールをインポート

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/pagination';

const StyledSwiper = styled(Swiper)(({ theme }) => ({
  width: '100%',
  paddingBottom: '50px',
  overflow: 'visible', // オーバーフローを可視化
  '& .swiper-pagination-bullets': {
    bottom: '-20px', // ページネーションの位置を調整
  },
}));

const SummaryCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '16px 16px 10px 16px',
  border: '8px solid #1976d2', // 青系の色に変更
  borderRadius: '0px 16px 16px 16px',
  boxShadow: '2px 4px 4px rgba(0, 0, 0, 0.5)',
  transition: 'transform 0.3s ease',
  position: 'relative',
  overflow: 'visible', // オーバーフローを可視化
  '&:hover': {
    transform: 'scale(1.02)',
    boxShadow: '8px 8px 4px rgba(0, 0, 0, 0.5)',
  },
}));
const SummaryCardMobile = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  border: '8px solid #1976d2', // 青系の色に変更
  borderRadius: '0px 16px 16px 16px',
  boxShadow: '2px 4px 4px rgba(0, 0, 0, 0.5)',
  transition: 'transform 0.3s ease',
  position: 'relative',
  overflow: 'visible', // オーバーフローを可視化
  '&:hover': {
    transform: 'scale(1.02)',
    boxShadow: '8px 8px 4px rgba(0, 0, 0, 0.5)',
  },
}));
const SummaryLabel = styled(Box)(({ theme }) => ({
  position: 'absolute',
  display: 'inline-block',
  height: '20px',
  top: '-36px',
  left: '-8px',
  backgroundColor: '#1976d2', // 青系の色に変更
  color: '#ffffff',
  padding: theme.spacing(1, 2),
  borderRadius: '8px 8px 0 0',
  fontWeight: 'bold',
  fontSize: '1.2rem',
}));
const SummaryLabelMobile = styled(Box)(({ theme }) => ({
  position: 'absolute',
  display: 'inline-block',
  height: '20px',
  top: '-36px',
  left: '-8px',
  backgroundColor: '#1976d2', // 青系の色に変更
  color: '#ffffff',
  padding: theme.spacing(1, 2),
  borderRadius: '8px 8px 0 0',
  fontWeight: 'bold',
  fontSize: '1.2rem',
}));

interface SummaryProps {
  id?: string; // idプロパティを追加
}
interface SummaryCardItem {
  title: string;
  description: string;
  image: string;
}
const summaryItems: SummaryCardItem[] = [
  {
    title: '業務効率化',
    description: 'インフォメーション業務の削減<br/>24h体制・多言語対応の実現',
    image: '/images/Summary/1.svg', // 画像のパスを適宜変更
  },
  {
    title: '顧客満足度向上',
    description: '顧客の体験を向上させる<br/>インタラクティブなコンテンツ提供',
    image: '/images/Summary/2.svg', // 画像のパスを適宜変更
  },
  {
    title: 'コスト削減',
    description: 'チラシやパンフレットのコストを削減<br/>フロアガイド変更工事を不要に',
    image: '/images/Summary/3.svg', // 画像のパスを適宜変更
  },
];

const ContactButton = () => {
  return (
    <Button
      variant='contained'
      size='large'
      sx={{ width: '400px', borderRadius: '20px', mt: 4 }} // マージンを追加
    >
      <Typography variant="h6" component="span" fontWeight={'600'}>
        資料請求
      </Typography>
      <DraftsIcon sx={{ ml: 1 }} />
    </Button>
  )
}

const Summary: React.FC<SummaryProps> = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // isMobileを追加

  return (
    <Container maxWidth="md"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: theme.spacing(4),
        width: '100vw',
        height: '100vh',
        padding: theme.spacing(4),
      }}
      >
      <Typography variant="h3" component="h2" gutterBottom>
        Nextiの導入メリットまとめ
      </Typography>
      {isMobile ? (
        <StyledSwiper
          modules={[Pagination]}
          spaceBetween={30}
          slidesPerView={1}
          pagination={{ clickable: true }}
        >
          {summaryItems.map((item, index) => (
            <SwiperSlide 
              key={index}
              style = {{ overflow: 'visible'}}
            >
              <SummaryCardMobile
                sx={{
                  // 色を少しずつグラーデーションに
                  borderColor: index === 2 ? '#1976d2' : index === 1 ? '#42a5f5' : '#90caf9', // 青系の色に変更
                }}>
                <SummaryLabelMobile
                  sx={{
                    backgroundColor: index === 2 ? '#1976d2' : index === 1 ? '#42a5f5' : '#90caf9', // 青系の色に変更
                    color: '#ffffff', // ラベルの文字色を白に設定
                  }}
                > {/* SummaryLabelを追加 */}
                  {item.title} {/* タイトルを表示 */}
                </SummaryLabelMobile>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="text.primary" fontWeight="bold" gutterBottom>
                    {item.description.split('<br/>').map((line, i) => (
                      <span key={i}>
                        {line}
                        {i < item.description.split('<br/>').length - 1 && <br />}
                      </span>
                    ))}
                  </Typography>
                </CardContent>
                <img src={item.image} alt={item.title} style={{ width: '100%', aspectRatio: '1/1', borderRadius: '8px' }} />
              </SummaryCardMobile>
            </SwiperSlide>
          ))}
        </StyledSwiper>
      ) : (
        <Grid container 
          spacing={4} 
          sx={{mt:4, height: '45vh' }}
        >
          {summaryItems.map((item, index) => (
            <Grid item xs={12} sm={6} md={4} key={index} sx={{height:'100%'}}>
              <SummaryCard
                sx={{
                  // 色を少しずつグラーデーションに
                  borderColor: index === 2 ? '#1976d2' : index === 1 ? '#42a5f5' : '#90caf9', // 青系の色に変更
                }}>
                <SummaryLabel
                  sx={{
                    backgroundColor: index === 2 ? '#1976d2' : index === 1 ? '#42a5f5' : '#90caf9', // 青系の色に変更
                    color: '#ffffff', // ラベルの文字色を白に設定
                  }}
                > {/* SummaryLabelを追加 */}
                  {item.title} {/* タイトルを表示 */}
                </SummaryLabel>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="body1" color="text.primary" fontWeight="bold" gutterBottom>
                    {item.description.split('<br/>').map((line, i) => (
                      <span key={i}>
                        {line}
                        {i < item.description.split('<br/>').length - 1 && <br />}
                      </span>
                    ))}
                  </Typography>
                </CardContent>
                <img src={item.image} alt={item.title} style={{ width: '100%', aspectRatio: '1/1', borderRadius: '8px' }} />
              </SummaryCard>
            </Grid>
          ))}
        </Grid>
      )}
      <ContactButton />
    </Container>
  );
};

export default Summary;
